import { useEffect, useCallback } from "react";
import { SUPPORT_CONFIG } from "@finitless/shared/supportConstants";

const CANNY_SCRIPT_ID = "canny-jssdk";
const CANNY_SDK_URL = "https://sdk.canny.io/sdk.js";

/**
 * Install Canny stub queue + loader (matches https://developers.canny.io/install).
 * @param {Window} w
 */
const ensureCannyStub = (w) => {
  if (typeof w.Canny === "function") {
    return;
  }
  const stub = function stubbedCanny() {
    stub.q.push(arguments);
  };
  stub.q = [];
  w.Canny = stub;
};

/**
 * Load Canny SDK script once (idempotent).
 */
const loadCannyScript = () => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(CANNY_SCRIPT_ID)) {
    return;
  }
  const firstScript = document.getElementsByTagName("script")[0];
  const script = document.createElement("script");
  script.id = CANNY_SCRIPT_ID;
  script.type = "text/javascript";
  script.async = true;
  script.src = CANNY_SDK_URL;
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

/**
 * Custom hook for Canny integration (identify users for data-canny-link flows).
 * @see https://developers.canny.io/install
 */
export const useCanny = () => {
  const { canny } = SUPPORT_CONFIG;

  useEffect(() => {
    if (!canny.enabled || !canny.appId) {
      return;
    }

    ensureCannyStub(window);
    if (document.readyState === "complete") {
      loadCannyScript();
    } else {
      window.addEventListener("load", loadCannyScript, false);
      return () => {
        window.removeEventListener("load", loadCannyScript, false);
      };
    }
  }, [canny.appId, canny.enabled]);

  const identify = useCallback(
    (userData) => {
      if (!canny.appId) {
        return;
      }
      ensureCannyStub(window);
      const createdIso =
        userData.created != null
          ? new Date(userData.created).toISOString()
          : new Date().toISOString();

      window.Canny("identify", {
        appID: canny.appId,
        user: {
          email: userData.email,
          name: userData.name,
          id: String(userData.id),
          ...(userData.avatarURL && { avatarURL: userData.avatarURL }),
          created: createdIso,
        },
      });
    },
    [canny.appId],
  );

  /**
   * Canny does not document a client logout API; reserved for future SDK behavior.
   */
  const reset = useCallback(() => {}, []);

  return {
    identify,
    reset,
    isEnabled: canny.enabled && !!canny.appId,
  };
};

export default useCanny;
